/**
 * Commerce Disclosure Page (特定商取引法に基づく表記)
 * Required disclosure for online commerce in Japan under the Specified Commercial Transactions Act
 *
 * This page is required by Japanese law and must always be displayed in Japanese,
 * regardless of the user's language preference.
 */

import { Store, CreditCard, Clock, RefreshCw, Monitor, Mail } from 'lucide-react';
import { translate } from '@/lib/i18n';
import './docs.scss';

export function CommerceDisclosure() {
  // Always Japanese (required by Japanese law). The page reads its own copy in
  // Japanese rather than switching the site locale, which used to overwrite the
  // visitor's stored language choice for every page after this one.
  const t = (key: string) => translate('ja', key);

  const priceItems = t('commerce.price.items').split('|');
  const paymentItems = t('commerce.payment.items').split('|');
  const deliveryItems = t('commerce.delivery.items').split('|');
  const environmentItems = t('commerce.environment.items').split('|');

  return (
    <div className="docs-content commerce-page" lang="ja">
      <h1>{t('commerce.title')}</h1>
      <p><em>{t('commerce.lastUpdated')}</em></p>

      {/* Business Operator Information */}
      <section className="commerce-page__section">
        <h2>
          <Store size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('commerce.operator.title')}
        </h2>
        <table className="commerce-page__table">
          <tbody>
            <tr>
              <th>{t('commerce.operator.name.label')}</th>
              <td>{t('commerce.operator.name.value')}</td>
            </tr>
            <tr>
              <th>{t('commerce.operator.representative.label')}</th>
              <td>{t('commerce.operator.representative.value')}</td>
            </tr>
            <tr>
              <th>{t('commerce.operator.address.label')}</th>
              <td>{t('commerce.operator.address.value')}</td>
            </tr>
            <tr>
              <th>{t('commerce.operator.phone.label')}</th>
              <td>{t('commerce.operator.phone.value')}</td>
            </tr>
            <tr>
              <th>{t('commerce.operator.email.label')}</th>
              <td>
                <a href={`mailto:${t('commerce.operator.email.value')}`}>
                  {t('commerce.operator.email.value')}
                </a>
              </td>
            </tr>
            <tr>
              <th>{t('commerce.operator.url.label')}</th>
              <td>
                <a href={t('commerce.operator.url.value')} target="_blank" rel="noopener noreferrer">
                  {t('commerce.operator.url.value')}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Price */}
      <section className="commerce-page__section">
        <h2>
          <CreditCard size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('commerce.price.title')}
        </h2>
        <p>{t('commerce.price.description')}</p>
        <ul>
          {priceItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <div className="commerce-page__highlight">
          <p>{t('commerce.price.tax')}</p>
        </div>
      </section>

      {/* Additional Fees */}
      <section className="commerce-page__section">
        <h2>{t('commerce.additionalFees.title')}</h2>
        <p>{t('commerce.additionalFees.description')}</p>
      </section>

      {/* Payment Methods */}
      <section className="commerce-page__section">
        <h2>{t('commerce.payment.title')}</h2>
        <ul>
          {paymentItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <p>{t('commerce.payment.note')}</p>
      </section>

      {/* Payment Period */}
      <section className="commerce-page__section">
        <h2>{t('commerce.paymentPeriod.title')}</h2>
        <p>{t('commerce.paymentPeriod.content')}</p>
      </section>

      {/* Service Delivery */}
      <section className="commerce-page__section">
        <h2>
          <Clock size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('commerce.delivery.title')}
        </h2>
        <ul>
          {deliveryItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </section>

      {/* Refund / Exchange Policy */}
      <section className="commerce-page__section">
        <h2>
          <RefreshCw size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('commerce.refund.title')}
        </h2>
        <div className="commerce-page__subsection">
          <h3>{t('commerce.refund.customerRequest.title')}</h3>
          <p>{t('commerce.refund.customerRequest.content')}</p>
        </div>
        <div className="commerce-page__subsection">
          <h3>{t('commerce.refund.defect.title')}</h3>
          <p>{t('commerce.refund.defect.content')}</p>
        </div>
      </section>

      {/* Operating Environment */}
      <section className="commerce-page__section">
        <h2>
          <Monitor size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('commerce.environment.title')}
        </h2>
        <ul>
          {environmentItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </section>

      {/* Contact */}
      <section className="commerce-page__section">
        <h2>
          <Mail size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('commerce.contact.title')}
        </h2>
        <p>{t('commerce.contact.description')}</p>
        <div className="commerce-page__contact">
          <p>{t('commerce.contact.email')}</p>
          <p>{t('commerce.contact.github')}</p>
        </div>
      </section>
    </div>
  );
}
